import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cookbookPath } from "@/lib/slug";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import { BookOpenText, KeyReturn, Camera, PencilSimple, LinkSimple, Books, DeviceMobile, CalendarCheck, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BookCover from "@/components/BookCover";
import { FavouritesBookCard } from "@/components/FavouritesBookCard";
import BookShelf from "@/components/BookShelf";
import ReferFriendsCard from "@/components/ReferFriendsCard";
import MyImports from "@/components/MyImports";
import ImportNoticeBanner from "@/components/ImportNoticeBanner";
import FamilyExpiryBanner from "@/components/FamilyExpiryBanner";

const PRIVACY_OPTIONS = [
  { value: "public", label: "Public — list in directory" },
  { value: "all_pages_shared", label: "All pages shared (private invite)" },
  { value: "select_pages_shared", label: "Selected pages shared" },
  { value: "selective_minus_secrets", label: "All except 'Secret' recipes" },
];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { flags, limits } = useSiteFlags();
  const FREE = limits?.free_recipe_limit ?? 5;
  // Effective cap includes any recipe slots the user has earned from their
  // referral programme. This is what the "recipes remaining" copy should
  // count against, not the raw baseline. Falls back to FREE if the field
  // is missing (legacy user object).
  const referralBonus = user?.referral_bonus_recipes || 0;
  const EFFECTIVE_FREE = FREE + referralBonus;
  const nav = useNavigate();
  const [tab, setTab] = useState("view");
  const [cookbooks, setCookbooks] = useState({ owned: [], co_edited: [], viewing: [], invited: [] });
  const [trashCount, setTrashCount] = useState(0);
  const [code, setCode] = useState("");
  const [newCb, setNewCb] = useState({ title: "", subtitle: "", description: "", privacy: "public" });
  const [cookbookView, setCookbookView] = useState(() => localStorage.getItem("rf_cb_view") || "grid");
  // Backfill AI-summaries across every cookbook the user OWNS (not
  // co-edits). Populated after the caller clicks the button; polled
  // every 3s until it finishes.
  const [summaryJob, setSummaryJob] = useState(null);
  // Same shape, for the nutrition backfill job.
  const [nutritionJob, setNutritionJob] = useState(null);

  const switchView = (v) => {
    setCookbookView(v);
    try { localStorage.setItem("rf_cb_view", v); } catch { /* ignore quota */ }
  };
  const [open, setOpen] = useState(false);
  const [clipboardUrl, setClipboardUrl] = useState(""); // detected recipe-shaped URL from clipboard
  const [clipDismissed, setClipDismissed] = useState(false);
  // iOS Share-Setup nudge — dismissible via localStorage. Shown once per device
  // to logged-in iOS users; encourages them to install the shortcut.
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [iosNudgeDismissed, setIosNudgeDismissed] = useState(() => {
    try { return localStorage.getItem("rf_ios_nudge_dismissed") === "1"; } catch { return false; }
  });
  const dismissIosNudge = () => {
    try { localStorage.setItem("rf_ios_nudge_dismissed", "1"); } catch { /* quota */ }
    setIosNudgeDismissed(true);
  };

  const refresh = async () => {
    const { data } = await api.get("/cookbooks");
    // Backfill back-compat shape for older API responses.
    setCookbooks({
      owned: data.owned || [],
      co_edited: data.co_edited || [],
      viewing: data.viewing || (data.invited || []),
      invited: data.invited || [],
      favourites_count: data.favourites_count || 0,
    });
    // Trash count — lets us show / hide the "View Trash (N)" link
    // without a separate roundtrip from the Trash page. Silent on
    // failure so an older account whose data layer hasn't migrated
    // never breaks the Dashboard header.
    try {
      const [t, rt] = await Promise.all([api.get("/cookbooks/trash"), api.get("/me/recipe-trash").catch(() => ({ data: [] }))]);
      setTrashCount((Array.isArray(t.data) ? t.data.length : 0) + (Array.isArray(rt.data) ? rt.data.length : 0));
    } catch { setTrashCount(0); }
  };

  useEffect(() => { if (user) refresh(); }, [user]);

  // Auto-refresh on tab focus / visibility change / 25s poll so the
  // Inbox card (and every other cookbook count) updates the moment the
  // cook returns from Instagram / Shortcuts. Mirrors CookbookView. Signal
  // of "new recipe arrived" is `user.recipe_count` — it increments on
  // every save. Note: `/cookbooks` doesn't return per-cookbook counts, so
  // we can't sum owned recipes. `/auth/me` does return the global count.
  useEffect(() => {
    if (!user) return undefined;
    let lastCount = user.recipe_count || 0;
    let pollTimer = null;
    let cancelled = false;

    const softRefresh = async () => {
      try {
        // Hit /auth/me directly (bypasses AuthContext's 30s throttle for
        // this specific poll — we WANT more frequent checks here).
        const me = await api.get("/auth/me");
        if (cancelled) return;
        const nextCount = me.data?.recipe_count || 0;
        if (nextCount > lastCount) {
          const added = nextCount - lastCount;
          toast.success(
            added === 1 ? "New recipe just landed 📥" : `${added} new recipes just landed 📥`,
            { description: "Fresh from your iOS share — check your Inbox.", duration: 5000 },
          );
          // Refresh the cookbook list so the Inbox card visibly updates.
          await refresh();
        }
        lastCount = nextCount;
      } catch {
        /* silent — transient network blips are fine; next poll retries */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") softRefresh();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", softRefresh);
    pollTimer = setInterval(() => {
      if (document.visibilityState === "visible") softRefresh();
    }, 25000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", softRefresh);
      if (pollTimer) clearInterval(pollTimer);
    };
    // Only re-arm when the user identity changes; the closure captures
    // `refresh` which is stable across renders (defined at module scope).
  }, [user?.user_id]);

  // Backfill AI 30-word summaries across every cookbook the user OWNS.
  // Excludes co-edited cookbooks by design — the primary owner should
  // trigger those. Polls status every 3s until done.
  const runBackfillAllSummaries = async () => {
    try {
      const { data } = await api.post("/me/recipes/backfill-summary", {});
      if (data.status === "noop") {
        toast(data.message || "Every recipe already has a summary.");
        return;
      }
      setSummaryJob({ ...data, processed: 0 });
      toast(`Drafting summaries across ${data.cookbook_count} cookbook${data.cookbook_count === 1 ? "" : "s"} — ${data.requested} recipe${data.requested === 1 ? "" : "s"} to go.`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: status } = await api.get(`/me/recipes/backfill-summary/status/${jobId}`);
          setSummaryJob(status);
          if (status.status === "done") {
            toast.success(`Summaries drafted: ${status.inserted || 0} written, ${status.skipped || 0} skipped.`);
            return;
          }
          if (status.status === "error") {
            toast.error(status.error || "Backfill failed");
            return;
          }
          if (status.status === "cancelled") return;
          setTimeout(poll, 3000);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Poll failed");
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start backfill");
    }
  };

  // Backfill AI nutrition estimates across every cookbook the user OWNS.
  // Mirrors `runBackfillAllSummaries` — same polling cadence, same scope
  // (owner only). Useful for recipes created BEFORE auto-nutrition was
  // added so the full catalogue picks up a macro card.
  const runBackfillAllNutrition = async () => {
    try {
      const { data } = await api.post("/me/recipes/backfill-nutrition", {});
      if (data.status === "noop") {
        toast(data.message || "Every recipe already has a nutrition estimate.");
        return;
      }
      setNutritionJob({ ...data, processed: 0 });
      toast(`Estimating nutrition across ${data.cookbook_count} cookbook${data.cookbook_count === 1 ? "" : "s"} — ${data.requested} recipe${data.requested === 1 ? "" : "s"} to go.`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: status } = await api.get(`/me/recipes/backfill-nutrition/status/${jobId}`);
          setNutritionJob(status);
          if (status.status === "done") {
            toast.success(`Nutrition estimates added: ${status.inserted || 0} written, ${status.skipped || 0} skipped.`);
            return;
          }
          if (status.status === "error") {
            toast.error(status.error || "Backfill failed");
            return;
          }
          if (status.status === "cancelled") return;
          setTimeout(poll, 3000);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Poll failed");
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start backfill");
    }
  };

  // Auto-redeem when the user arrives via an email-invite link.
  // Modern flow: ?invite_token=XYZ — single-use, kind locked by the server,
  //                                  co-editor gated to paid tiers.
  // Legacy flow: ?invite_code=ABC (10-digit) — view-only fallback.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite_token");
    const legacyCode = params.get("invite_code");
    if (!token && !legacyCode) return;

    const stripParams = () => {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("invite_token");
      clean.searchParams.delete("invite_code");
      clean.searchParams.delete("kind");
      window.history.replaceState({}, "", clean.toString());
    };

    (async () => {
      try {
        if (token) {
          const { data } = await api.post("/cookbooks/redeem-invite", { token });
          toast.success(data.role === "co_editor" ? "You're now a co-editor!" : "Invite accepted!");
          await refresh();
          stripParams();
          nav(cookbookPath(data));
        } else {
          // Legacy code from older emails / QR shares — view-only only.
          setCode(legacyCode);
          setTab("mybooks");
          stripParams();
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        if (status === 402) {
          toast.error(detail || "Co-editing requires a paying-member plan. Upgrade and click the invite link again.");
        } else if (status === 410) {
          toast.error(detail || "This invite has already been used or has expired.");
        } else {
          toast.error(detail || "Invite couldn't be redeemed.");
        }
        stripParams();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on user+URL params; nav is stable and re-running on nav change would re-redeem invite tokens
  }, [user]);

  // Clipboard-detect on focus/visibility — gives iOS users a one-tap import from any
  // social app where they tapped "Copy link". Silently no-ops on permission denial.
  useEffect(() => {
    if (!user) return;
    const KEY = "rf_clipboard_dismissed_url";
    const check = async () => {
      try {
        const txt = await navigator.clipboard?.readText?.();
        if (!txt) return;
        const m = txt.match(/https?:\/\/\S+/i);
        if (!m) return;
        const u = m[0].replace(/[).,"'`]+$/, "");
        // Heuristic: only suggest URLs that look like recipe/social posts.
        const RECIPE_SHAPED = /(instagram\.com\/p\/|instagram\.com\/reel\/|tiktok\.com\/|pinterest\.|recipe|cooking|food|kitchen)/i;
        if (!RECIPE_SHAPED.test(u)) return;
        if (sessionStorage.getItem(KEY) === u) return; // already dismissed for this session
        setClipboardUrl(u);
      } catch { /* permission denied — silent */ }
    };
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user]);

  const dismissClipboard = () => {
    sessionStorage.setItem("rf_clipboard_dismissed_url", clipboardUrl);
    setClipDismissed(true);
    setClipboardUrl("");
  };

  // Wait for the auth check to settle before deciding which screen to render —
  // otherwise a logged-in user hitting Back briefly sees "Please sign in" because
  // checkAuth() hasn't resolved yet (~200ms), which feels like a silent logout.
  if (loading) return (
    <div className="max-w-md mx-auto p-12 text-center" data-testid="dashboard-auth-loading">
      <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin mx-auto" aria-hidden="true" />
    </div>
  );

  if (!user) return (
    <div className="max-w-md mx-auto p-12 text-center">
      <p className="text-rf-ink-soft mb-4">Please sign in to access My Cookbooks.</p>
      <Link to="/login" className="btn-amber">Sign in</Link>
    </div>
  );

  const createBook = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cookbooks", newCb);
      toast.success("Cookbook created!");
      setOpen(false);
      setNewCb({ title: "", subtitle: "", description: "", privacy: "public" });
      await refresh();
      nav(cookbookPath(data));
    } catch (e) {
      toast.error("Failed to create");
    }
  };

  const redeemCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/cookbooks/redeem-code", { code });
      toast.success("Invite accepted as view-only.");
      setCode("");
      await refresh();
      nav(cookbookPath(data));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Invalid code");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <AutoSeo
        pageKey="my_cookbooks"
        fallbackTitle="My cookbooks — your family recipe library"
        fallbackDescription="Every cookbook you've built, every recipe you've saved — all in one place."
        noindex
      />

      <FamilyExpiryBanner />

      {isIOS && user && !iosNudgeDismissed && (
        <div data-testid="ios-nudge-banner" className="mb-6 rounded-2xl bg-gradient-to-r from-rf-amber/30 to-rf-amber/10 border border-rf-amber/50 p-4 flex items-start gap-3">
          <DeviceMobile size={24} weight="duotone" className="text-rf-orange shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-rf-ink">Turn your iPhone into a scanner.</p>
            <p className="text-xs text-rf-ink-soft mt-0.5 leading-relaxed">
              Install the free Recipe Filer shortcut so every recipe you find on Safari, TikTok, Instagram or YouTube saves to your Inbox with one tap.
            </p>
          </div>
          <Link
            to="/ios-share-setup"
            data-testid="ios-nudge-cta"
            className="btn-amber !py-2 !px-3 !text-sm shrink-0 whitespace-nowrap"
          >
            Set up →
          </Link>
          <button data-testid="ios-nudge-dismiss" onClick={dismissIosNudge} className="text-rf-ink-soft hover:text-rf-ink p-2 shrink-0" aria-label="Dismiss">✕</button>
        </div>
      )}

      {clipboardUrl && !clipDismissed && (
        <div data-testid="clipboard-banner" className="mb-6 rounded-2xl bg-rf-amber/20 border border-rf-amber/40 p-4 flex items-start gap-3">
          <LinkSimple size={24} weight="duotone" className="text-rf-orange shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-rf-ink">Import this link from your clipboard?</p>
            <p className="text-xs text-rf-ink-soft truncate font-mono mt-0.5">{clipboardUrl}</p>
          </div>
          <Link
            to={`/scan?mode=url&url=${encodeURIComponent(clipboardUrl)}`}
            data-testid="clipboard-import-btn"
            className="btn-amber !py-2 !px-3 !text-sm shrink-0"
            onClick={() => sessionStorage.setItem("rf_clipboard_dismissed_url", clipboardUrl)}
          >
            Import
          </Link>
          <button data-testid="clipboard-dismiss-btn" onClick={dismissClipboard} className="text-rf-ink-soft hover:text-rf-ink p-2 shrink-0" aria-label="Dismiss">✕</button>
        </div>
      )}

      {user.frozen && (
        <div className="mb-6 card-recipe !bg-amber-100 border-amber-400" data-testid="frozen-account-banner">
          <p className="font-display font-extrabold text-amber-900 text-lg mb-1">❄ Account frozen</p>
          <p className="text-sm text-amber-900/85">An administrator has frozen your account. You can still view, edit, and delete your existing recipes and cookbooks, but you can&apos;t add new ones. If you believe this is an error, please contact support.</p>
        </div>
      )}

      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">My Cookbooks</p>
          <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink">Welcome, {user.name.split(" ")[0]}.</h1>
          <p className="text-rf-ink-soft mt-2">
            {user.subscription_tier === "free" ? (
              flags.subscriptions_enabled ? (
                <>You&apos;re on the free tier — {Math.max(0, EFFECTIVE_FREE - user.recipe_count)} of {EFFECTIVE_FREE} free saved recipes remaining{referralBonus ? <> (includes <b>+{referralBonus} referral bonus</b>)</> : null}. Scans, URL imports and drafts don&apos;t count until you save them. <Link to="/pricing" className="font-bold text-rf-orange">Upgrade</Link></>
              ) : (
                <>You&apos;re on the free tier — {Math.max(0, EFFECTIVE_FREE - user.recipe_count)} of {EFFECTIVE_FREE} free saved recipes remaining{referralBonus ? <> (includes <b>+{referralBonus} referral bonus</b>)</> : null}. Scans, URL imports and drafts don&apos;t count until you save them. Subscriptions launch soon — we&apos;ll let you know.</>
              )
            ) : user.subscription_tier === "lifetime" ? (
              <>You&apos;re on the <span className="font-bold text-emerald-700">★ Lifetime</span> plan — unlimited recipes &amp; cookbooks, free forever. Cook away.</>
            ) : (
              <>You&apos;re on the <span className="font-bold text-rf-orange">{({"1mo":"1-month","3mo":"3-month","6mo":"6-month","annual":"12-month"})[user.subscription_tier] || user.subscription_tier}</span> plan. Unlimited recipes — cook away.</>
            )}
          </p>
          {/* Soft warning banner at 8+/10 recipes so hitting the wall on
              recipe 11 isn't a surprise. Only shown to free-tier users. */}
          {user.subscription_tier === "free" && user.recipe_count >= EFFECTIVE_FREE - 2 && user.recipe_count < EFFECTIVE_FREE && (
            <div
              data-testid="free-tier-warning"
              className="mt-4 rounded-2xl border-2 border-rf-amber bg-rf-amber/20 px-4 py-3 flex items-start gap-3 max-w-2xl"
            >
              <span className="text-2xl leading-none">⚠️</span>
              <div className="flex-1">
                <p className="font-display font-extrabold text-rf-ink text-sm">
                  Only {EFFECTIVE_FREE - user.recipe_count} free saved {EFFECTIVE_FREE - user.recipe_count === 1 ? "recipe" : "recipes"} left
                </p>
                <p className="text-xs text-rf-ink-soft mt-0.5">
                  Scanning and URL previews stay free — only recipes you tap <b>Save</b> on count toward your {EFFECTIVE_FREE}. Once you hit {EFFECTIVE_FREE}, saving new ones will be paused until you {flags.subscriptions_enabled ? <><Link to="/pricing" className="font-bold text-rf-orange">pick a plan</Link>.</> : <>subscribe (launching soon).</>}
                </p>
              </div>
            </div>
          )}
          {user.subscription_tier === "free" && user.recipe_count >= EFFECTIVE_FREE && (
            <div
              data-testid="free-tier-limit-hit"
              className="mt-4 rounded-2xl border-2 border-red-500/60 bg-red-50 px-4 py-3 flex items-start gap-3 max-w-2xl"
            >
              <span className="text-2xl leading-none">🚫</span>
              <div className="flex-1">
                <p className="font-display font-extrabold text-red-800 text-sm">
                  You&apos;ve saved all {EFFECTIVE_FREE} free recipes
                </p>
                <p className="text-xs text-red-700/90 mt-0.5">
                  Existing recipes stay yours forever, and you can still scan & preview new ones — but <b>saving</b> a new recipe is paused until you <Link to="/pricing" className="font-bold underline">pick a plan</Link>. Upgrade to save more.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Refer friends card — hidden unless Site Config has referrals_enabled=true.
          Free users get 5 extra recipe slots per qualifying referral (max 15
          referrals → +75 slots). Paid users still see it (for leaderboard credit). */}
      {flags.referrals_enabled && (
        <div className="mb-10 max-w-2xl">
          <ReferFriendsCard />
        </div>
      )}
      <ImportNoticeBanner />
      <MyImports compact />

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <div className="flex bg-white rounded-full p-1 border border-rf-ink/10 w-fit">
          <button data-testid="dash-tab-view" onClick={() => setTab("view")} className={`px-5 py-2.5 rounded-full text-sm font-bold ${tab === "view" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}>View Cookbooks</button>
          <button data-testid="dash-tab-create" onClick={() => setTab("create")} className={`px-5 py-2.5 rounded-full text-sm font-bold ${tab === "create" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}>Create a Recipe Book</button>
        </div>
        {flags.print_orders_enabled && (
          <Link to="/order-hardback" data-testid="dash-order-hardback" className="btn-outline-ink !py-2.5 !px-4 text-sm">
            Order a hardback →
          </Link>
        )}
        <Link
          to="/dashboard/trash"
          data-testid="dash-trash-link"
          className="btn-outline-ink !py-2.5 !px-4 text-sm !border-amber-300 !text-amber-800 hover:!bg-amber-50"
          title={trashCount ? `${trashCount} item${trashCount === 1 ? "" : "s"} in Trash — restorable for 30 days` : "Deleted cookbooks and recipes stay here for 30 days"}
        >
          Trash{trashCount ? ` (${trashCount})` : ""} →
        </Link>
        {/* Backfill 30-word AI "About this recipe" blurbs across every
            cookbook the user owns (excludes co-edited books — the primary
            owner should trigger those). Shows live progress while running. */}
        <button
          type="button"
          onClick={runBackfillAllSummaries}
          disabled={summaryJob?.status === "running"}
          data-testid="dash-backfill-summaries"
          className="btn-outline-ink !py-2.5 !px-4 text-sm"
          title="AI-drafts a 30-word intro for every recipe in your cookbooks that's missing one"
        >
          {summaryJob?.status === "running"
            ? `Adding summaries · ${summaryJob.processed || 0}/${summaryJob.total || 0}`
            : "Add AI summaries to all my recipes"}
        </button>
        {/* Nutrition backfill — runs the per-serving macro estimator on
            every recipe missing one. Separate from summaries so a cook can
            opt into just one. Live progress in the button label while
            the background job runs. */}
        <button
          type="button"
          onClick={runBackfillAllNutrition}
          disabled={nutritionJob?.status === "running"}
          data-testid="dash-backfill-nutrition"
          className="btn-outline-ink !py-2.5 !px-4 text-sm"
          title="AI-estimates per-serving kcal + macros for every recipe in your cookbooks that's missing one"
        >
          {nutritionJob?.status === "running"
            ? `Estimating nutrition · ${nutritionJob.processed || 0}/${nutritionJob.total || 0}`
            : "Add AI nutrition to all my recipes"}
        </button>
      </div>

      {/* Meal Planner discovery card — sits between the tab bar and content
          so cooks who missed the nav link discover the feature. Flag-gated,
          so it vanishes when Admin turns Meal Planner off. */}
      {flags.meal_planner_enabled && (
        <Link
          to="/planner"
          data-testid="dash-plan-week"
          className="block mb-8 rounded-3xl border border-rf-ink/10 bg-gradient-to-r from-rf-amber/25 to-rf-cream-alt/60 hover:from-rf-amber/40 hover:to-rf-cream-alt hover:border-rf-orange/50 transition p-5 sm:p-6 group"
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
              <CalendarCheck size={28} weight="duotone" className="text-rf-orange" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="overline mb-1">New · Weekly meal planner</p>
              <h3 className="font-display font-black text-rf-ink text-xl sm:text-2xl">
                Plan your week in a tap.
              </h3>
              <p className="text-rf-ink-soft text-sm mt-1">
                Drop recipes into breakfast, lunch, dinner and snacks — the shopping list writes itself.
              </p>
            </div>
            <span className="font-bold text-rf-orange text-sm group-hover:translate-x-0.5 transition-transform">
              Open planner →
            </span>
          </div>
        </Link>
      )}

      {tab === "create" ? (
        <div>
          {/* "Bring your data" — surfaces the /import hub prominently to
              signed-in users who often have recipes trapped in Paprika, Mela,
              Notion etc. Sits ABOVE the 4-card create grid because the
              biggest unlock most users can do is bring their existing
              collection over, not scan one at a time. */}
          <Link
            to="/import"
            data-testid="dash-bring-your-data"
            className="mb-8 block rounded-2xl border-2 border-rf-orange/30 bg-gradient-to-r from-rf-amber/20 via-white to-white p-5 sm:p-6 hover:border-rf-orange/60 hover:shadow-lg hover:shadow-rf-orange/10 transition-all group">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-rf-orange text-white flex items-center justify-center shrink-0 group-hover:rotate-3 transition-transform">
                <Sparkle size={28} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-1">Bring your data</p>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl text-rf-ink leading-tight">
                  Move recipes from Paprika, Mela, Notion & 26 more sources
                </h3>
                <p className="text-rf-ink-soft text-sm mt-1">
                  One-click imports for every recipe app, notes app, or website you've saved recipes in.
                  Photos rehosted, categories preserved.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-rf-orange font-bold shrink-0 group-hover:translate-x-1 transition-transform">
                Open Import <ArrowRight size={20} />
              </div>
            </div>
          </Link>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <Link to="/scan" data-testid="action-scan" className="card-recipe hover-lift block">
              <Camera size={40} weight="duotone" className="text-rf-orange mb-4" />
              <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-1">Past</p>
              <h3 className="font-display font-extrabold text-xl text-rf-ink mb-2">Scan a card</h3>
              <p className="text-rf-ink-soft text-sm">Photograph or upload a handwritten card, printed page or PDF. AI does the rest.</p>
            </Link>
            <Link to="/scan?mode=url" data-testid="action-link" className="card-recipe hover-lift block">
              <LinkSimple size={40} weight="duotone" className="text-rf-orange mb-4" />
              <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-1">Present</p>
              <h3 className="font-display font-extrabold text-xl text-rf-ink mb-2">Paste a link</h3>
              <p className="text-rf-ink-soft text-sm">Loved a recipe online? Paste the URL — from any cookery site, Instagram, TikTok or Pinterest.</p>
            </Link>
            <div className="relative">
              <Link to="/scan?mode=manual" data-testid="action-type" className="card-recipe hover-lift block">
                <PencilSimple size={40} weight="duotone" className="text-rf-orange mb-4" />
                <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-1">Living memory</p>
                <h3 className="font-display font-extrabold text-xl text-rf-ink mb-2">Type one in</h3>
                <p className="text-rf-ink-soft text-sm">Already know it by heart? Skip the AI and type — full editor on the next screen.</p>
                {/* Reserve space in the flow so absolute-positioned "paste text"
                    doesn't overlap the card copy on tall cards. */}
                <div className="h-6 mt-3" aria-hidden="true" />
              </Link>
              {/* Nested paste-text option — kept as a SIBLING (not inside the
                  outer <Link>) because nesting <a> inside <a> is invalid HTML
                  and React silently strips it. Absolute-positioned over the
                  reserved gap above so the visual affordance still reads as
                  "part of the Type one in card". */}
              <Link
                to="/scan?mode=paste"
                data-testid="action-paste"
                className="absolute bottom-6 left-6 text-xs font-bold text-rf-orange hover:underline z-10"
              >
                …or paste text →
              </Link>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="action-new-book" className="card-recipe hover-lift block text-left">
                  <Books size={40} weight="duotone" className="text-rf-orange mb-4" />
                  <p className="text-[11px] font-black tracking-[0.18em] uppercase text-rf-orange mb-1">Bookshelf</p>
                  <h3 className="font-display font-extrabold text-xl text-rf-ink mb-2">New cookbook</h3>
                  <p className="text-rf-ink-soft text-sm">Set a title, palette and privacy. Add recipes after.</p>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-rf-cream border-rf-ink/10">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-black text-rf-ink">New cookbook</DialogTitle>
                </DialogHeader>
                <form onSubmit={createBook} className="space-y-4">
                  <div>
                    <label className="overline">Title</label>
                    <input data-testid="new-cb-title" required value={newCb.title} onChange={(e) => setNewCb({ ...newCb, title: e.target.value })} className="input-rf mt-1" placeholder="e.g. The Lefevre family table" />
                  </div>
                  <div>
                    <label className="overline">Cover subtitle (optional)</label>
                    <input
                      data-testid="new-cb-subtitle"
                      value={newCb.subtitle || ""}
                      onChange={(e) => setNewCb({ ...newCb, subtitle: e.target.value })}
                      maxLength={120}
                      className="input-rf mt-1 italic"
                      placeholder="e.g. Provençal recipes, three generations"
                    />
                    <p className="text-[11px] text-rf-ink-soft mt-1">Printed under the title on the book cover. Leave blank for none.</p>
                  </div>
                  <div>
                    <label className="overline">Description</label>
                    <textarea data-testid="new-cb-desc" rows={3} value={newCb.description} onChange={(e) => setNewCb({ ...newCb, description: e.target.value })} className="input-rf mt-1" placeholder="Internal description (not shown on the cover)" />
                  </div>
                  <div>
                    <label className="overline">Privacy</label>
                    <select data-testid="new-cb-privacy" value={newCb.privacy} onChange={(e) => setNewCb({ ...newCb, privacy: e.target.value })} className="input-rf mt-1">
                      {PRIVACY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <button data-testid="new-cb-submit" className="btn-amber w-full">Create cookbook</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-rf-cream-alt rounded-3xl p-8 md:p-12">
            <p className="overline mb-3">Quick tip</p>
            <h3 className="font-display text-2xl md:text-3xl font-black text-rf-ink mb-3">Start with one recipe.</h3>
            <p className="text-rf-ink-soft max-w-2xl">
              Don&apos;t worry about building the perfect cookbook first. Scan a single favourite from grandma&apos;s drawer, paste a link from a chef you love, or type one you know by heart. The rest follows naturally.
            </p>
          </div>
        </div>
      ) : (
        <div>
          {cookbooks.owned.length === 0 && cookbooks.invited.length === 0 ? (
            <div className="card-recipe text-center py-16">
              <BookOpenText size={48} className="mx-auto text-rf-orange mb-4" />
              <p className="text-rf-ink-soft mb-4">No cookbooks yet.</p>
              <button onClick={() => setTab("create")} className="btn-amber">Create your first</button>
            </div>
          ) : (
            <>
              {cookbooks.owned.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="overline">Your cookbooks</h2>
                    <div className="inline-flex rounded-full bg-rf-cream-alt p-1" data-testid="cb-view-toggle">
                      <button
                        onClick={() => switchView("grid")}
                        data-testid="cb-view-grid"
                        className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-colors ${cookbookView === "grid" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft hover:text-rf-ink"}`}
                      >
                        Grid
                      </button>
                      <button
                        onClick={() => switchView("shelf")}
                        data-testid="cb-view-shelf"
                        className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-colors ${cookbookView === "shelf" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft hover:text-rf-ink"}`}
                      >
                        📚 Shelf
                      </button>
                    </div>
                  </div>
                  {cookbookView === "shelf" ? (
                    <>
                      <div className="w-44 mb-6"><FavouritesBookCard count={cookbooks.favourites_count} /></div>
                      <BookShelf cookbooks={cookbooks.owned} />
                    </>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FavouritesBookCard count={cookbooks.favourites_count} />
                      {cookbooks.owned.map((c) => (
                        <Link key={c.cookbook_id} to={cookbookPath(c)} data-testid={`cb-card-${c.cookbook_id}`} className="group block">
                          <BookCover cookbook={c} size="sm" className="group-hover:shadow-2xl transition-shadow group-hover:-translate-y-0.5 duration-200" />
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <span className="chip !text-[10px]">{c.privacy.replace(/_/g, " ")}</span>
                            <span className="chip !text-[10px]" data-testid={`cb-card-count-${c.cookbook_id}`}>{c.recipe_count ?? 0} {c.recipe_count === 1 ? "recipe" : "recipes"}</span>
                            {c.invite_code && <span className="text-[11px] font-mono text-rf-orange truncate">{c.invite_code}</span>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {cookbooks.co_edited.length > 0 && (
                <div className="mb-10" data-testid="dashboard-coedited-section">
                  <h2 className="overline mb-4">You&apos;re a co-editor on</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cookbooks.co_edited.map((c) => (
                      <Link key={c.cookbook_id} to={cookbookPath(c)} data-testid={`dashboard-coedited-${c.cookbook_id}`} className="group block">
                        <BookCover cookbook={c} size="sm" className="group-hover:shadow-2xl transition-shadow group-hover:-translate-y-0.5 duration-200" />
                        <div className="mt-3">
                          <span className="chip !text-[10px] !bg-blue-100 !text-blue-800">Co-editor</span>
                          <span className="chip !text-[10px] ml-2">{c.recipe_count ?? 0} {c.recipe_count === 1 ? "recipe" : "recipes"}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {cookbooks.viewing.length > 0 && (
                <div data-testid="dashboard-viewing-section">
                  <h2 className="overline mb-4">Shared with you (view-only)</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cookbooks.viewing.map((c) => (
                      <Link key={c.cookbook_id} to={cookbookPath(c)} data-testid={`dashboard-viewing-${c.cookbook_id}`} className="group block">
                        <BookCover cookbook={c} size="sm" className="group-hover:shadow-2xl transition-shadow group-hover:-translate-y-0.5 duration-200" />
                        <div className="mt-3">
                          <span className="chip !text-[10px]">View-only</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Invite-code redemption — positioned below the cookbook list so
              a user's own cookbooks show first (per user feedback 2026-07-08). */}
          <div className="bg-rf-cream-alt rounded-3xl p-5 mt-10" data-testid="redeem-card">
            <p className="overline mb-1">Have an invite code?</p>
            <p className="text-xs text-rf-ink-soft mb-3">
              Manual 10-digit codes unlock view-only access. <b>Co-editor invites only work via the unique link in the email</b> the owner sent you — paste it into your browser to accept.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-rf-ink-soft block mb-1">10-digit code</label>
                <input
                  data-testid="invite-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. 1234567890"
                  className="input-rf"
                />
              </div>
              <button data-testid="invite-code-redeem" onClick={redeemCode} className="btn-outline-ink !py-3"><KeyReturn size={18} /> Unlock view</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
